import { h } from 'preact';
import Store from 'store/store';
import { joinClasses, sortObjectByOrder } from 'utils/utils';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';

// eslint-disable-next-line no-unused-vars
import WatchPageEditor from 'components/editor/movies/watch/watch';
import SelectableSection from 'components/editor/selectableSection';
import IconPlus from 'components/icons/plus';
import DropdownField from 'components/editor/settings/dropdownField';
import SimpleField from 'components/editor/settings/simpleField';
import MediaField from 'components/editor/settings/mediaField';
import ToggleField from 'components/editor/settings/toggleField';
import Button from 'components/button/button';
import LinkIcon from 'components/icons/link';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import MoviePageHeader from 'components/editor/movies/movieHeader';
import { HOME_ENT_MERCHANTS } from 'components/editor/movies/utils';

const DEFAULT_DISCLAIMER_TEXT = "A digital purchase provides a limited license to access the content. See the retailer's terms for details.";

/**
 * A mixin that adds homeEnt-specific behavior to a WatchPageEditor.
 * Used in place of a subclass to avoid issues with multiple inheritance.
 * @template {typeof WatchPageEditor} T
 * @param {T} Base 
 * @returns {T}
 */
export const withHomeEntBehavior = (Base) => class extends Base {
	type = 'homeEnt';

	constructor(props) {
		super(props);

		this.addSection = this.addSection.bind(this);
		this.renderSection = this.renderSection.bind(this);
		this.onSectionsSort = this.onSectionsSort.bind(this);
		this.onSectionItemsSort = this.onSectionItemsSort.bind(this);
		this.resetFilter = this.resetFilter.bind(this);
		this.resetFilters = this.resetFilters.bind(this);
	}

	// ----- BASE ----- //

	/** OVERRIDE */
	getData() {
		return this.movie?.pages?.homeEnt || {};
	}

	/** OVERRIDE */
	getSettingsDetails() {
		const activeElement = this.props.activeElement;

		if (activeElement === '/pages/homeEnt/header') {
			return {
				nav: [{ label: 'Home ent header', path: '/pages/homeEnt/header' }],
				content: this.renderHeaderSettings(),
			};
		}

		const filterMatch = activeElement?.match(/^\/pages\/homeEnt\/filters\/([a-zA-Z0-9-]+)$/);
		if (filterMatch) {
			const [, filterId] = filterMatch;
			const nav = [
				{ label: 'Home ent header', path: '/pages/homeEnt/header' },
				{ label: 'Filter', path: `/pages/homeEnt/header/${filterId}` },
			];

			return { nav, content: this.renderFilterSettings(filterId) };
		}

		const sectionMatch = activeElement?.match(/^\/pages\/homeEnt\/sections(?:\/([a-zA-Z0-9-]+)(?:\/items\/([a-zA-Z0-9-]+))?)?$/);
		if (sectionMatch) {
			const [, sectionId, itemId] = sectionMatch;
			const data = this.getData();
			const nav = [
				{ label: 'Home ent', path: '/pages/homeEnt/sections' },
				sectionId && { label: data?.sections?.[sectionId]?.title, path: `/pages/homeEnt/sections/${sectionId}` },
				itemId && { label: this.getMerchantName(data?.sections?.[sectionId]?.items?.[itemId]), path: `/pages/homeEnt/sections/${sectionId}/items/${itemId}` },
			].filter(Boolean);

			if (!sectionId) return { nav, content: this.renderSectionsSettings() }; // If no section is selected, show sections list
			if (!itemId) return { nav, content: this.renderSectionSettings(sectionId) }; // If section is selected but no item is selected, show section settings
			return { nav, content: this.renderItemSettings(sectionId, itemId) }; // If both section and item are selected, show item settings
		}

		return super.getSettingsDetails();
	}

	// ----- MAIN CONTENT (MERCHANTS) ----- //

	// Item Utilities

	getMerchantName(item) {
		if (item?.useCustomMerchant) {
			return item?.customMerchant?.name || 'Custom merchant';
		}

		const merchant = item?.merchant && HOME_ENT_MERCHANTS.find(m => m.slug === item.merchant);
		return merchant?.label || 'New merchant';
	}

	getMerchantLogo(item, logoMode = 'light') {
		if (item.useCustomMerchant) {
			return item?.customMerchant?.image || null;
		}

		if (!item?.merchant) {
			return null;
		}

		return `${__SHARED_MEDIA_ROOT__}/images/boltHomeEntMerchants/${logoMode}/${item.merchant}_${logoMode}.png`;
	}

	addItem(section) {
		if (!section) return;

		const items = this.getData().sections?.[section]?.items || {};
		const itemKey = crypto.randomUUID();

		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/homeEnt/sections/${section}/items/${itemKey}`, {
			order: Math.max(0, ...Object.values(items).map(s => s.order || 0)) + 1,
			merchant: null,
			link: null,
		});
	}

	async deleteItem(section, item) {
		if (!section || !item) return;

		const promptResult = await confirm('Are you sure you want to delete this item?', { title: 'Delete item?', button: 'Delete', danger: true });
		if (!promptResult) return;

		jsonPointer.removeDeep(Store.get().data, `${this.props.dataRoot}/content/pages/homeEnt/sections/${section}/items/${item}`);
		this.props.select(`/pages/homeEnt/sections/${section}`);
	}

	selectItem(section, item) {
		if (!section || !item) return;

		this.props.select(`/pages/homeEnt/sections/${section}/items/${item}`);
	}

	onSectionItemsSort(e) {
		const section = e.target?.closest('[data-section]')?.dataset.section;
		if (!section) return;

		const basePath = this.props.dataRoot || '';
		const allItems = [...e.target.querySelectorAll('[data-item]')].map(el => el.dataset.item);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);
		allItems.forEach((id, index) => {
			jsonPointer.setDeep(Store.get().data, `${basePath}/content/pages/homeEnt/sections/${section}/items/${id}/order`, index + 1);
		});
	}

	// Section Utilities

	addSection() {
		const sections = this.getData().sections || {};
		const sectionKey = crypto.randomUUID();
		const itemKey = crypto.randomUUID();

		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/homeEnt/sections/${sectionKey}`, {
			order: Math.max(0, ...Object.values(sections).map(s => s.order || 0)) + 1,
			title: 'New',
			items: { [itemKey]: { order: 1, merchant: null, link: null } },
		});

		this.props.select(`/pages/homeEnt/sections`);
	}

	async deleteSection(section) {
		if (!section) return;

		const promptResult = await confirm('Are you sure you want to delete this section?', { title: 'Delete section?', button: 'Delete', danger: true });
		if (!promptResult) return;

		jsonPointer.removeDeep(Store.get().data, `${this.props.dataRoot}/content/pages/homeEnt/sections/${section}`);

		this.props.select(`/pages/homeEnt/sections`);
	}

	selectSection(section) {
		if (!section) return;

		this.props.select(`/pages/homeEnt/sections/${section}`);
	}

	onSectionsSort(e) {
		const basePath = this.props.dataRoot || '';
		const allItems = [...e.target.querySelectorAll('[data-section]')].map(el => el.dataset.section);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);
		allItems.forEach((id, index) => {
			jsonPointer.setDeep(Store.get().data, `${basePath}/content/pages/homeEnt/sections/${id}/order`, index + 1);
		});
	}

	// Render Visuals

	renderItem(sectionKey, [key, item]) {
		const image = this.getMerchantLogo(item);

		return (
			<SelectableSection class={this.s.sectionItem} section={`/pages/homeEnt/sections/${sectionKey}/items/${key}`} {...this.props} key={key}>
				{image && <img src={image} alt="" />}
			</SelectableSection>
		);
	}

	renderSection([sectionKey, sectionData]) {
		const entries = sortObjectByOrder(sectionData?.items, (v, k) => [k, v]);

		return (
			<SelectableSection class={this.s.section} section={`/pages/homeEnt/sections/${sectionKey}`} {...this.props} key={sectionKey}>
				<div class={this.s.sectionTitle}>{sectionData.title}</div>
				<div class={this.s.sectionContent}>
					{entries.map(entry => this.renderItem(sectionKey, entry))}
				</div>
				{sectionData.showDisclaimer && <div class={this.s.disclaimer}>{sectionData.disclaimer || DEFAULT_DISCLAIMER_TEXT}</div>}
			</SelectableSection>
		);
	}

	/** OVERRIDE */
	renderMainContent() {
		const data = this.getData();
		const sections = sortObjectByOrder(data.sections, (v, k) => [k, v]);

		if (!sections.length) {
			return (
				<button class={joinClasses(this.s.homeEntWrapper, this.s.add)} onClick={this.addSection}>
					<IconPlus /><span>Add section</span>
				</button>
			);
		}

		return (
			<SelectableSection class={this.s.homeEntWrapper} section="/pages/homeEnt/sections" {...this.props}>
				{sections.map(this.renderSection)}
			</SelectableSection>
		);
	}

	// Render Settings

	renderItemSettings(section, item) {
		const root = `${this.props.dataRoot}/content/pages/homeEnt/sections/${section}/items/${item}`;
		const data = this.getData()?.sections?.[section]?.items?.[item];
		const useCustomMerchant = data?.useCustomMerchant;
		const customMerchantName = data?.customMerchant?.name;

		return (
			<div class={this.s.itemSettings} data-section={section} data-item={item}>
				<DropdownField
					dark
					class={this.s.merchant}
					label="Merchant"
					path={`${root}/merchant`}
					placeholder="Select merchant..."
					searchable
					searchPlaceholder="Search merchants..."
					onChange={this.setMerchant}
					disabled={useCustomMerchant}
				>
					{HOME_ENT_MERCHANTS.map(merchant => <option value={merchant.slug} key={merchant.slug}>{merchant.label}</option>)}
				</DropdownField>
				<ToggleField dark path={`${root}/useCustomMerchant`} key="toggleCustom">Use custom merchant</ToggleField>
				<div class={joinClasses(this.s.customMerchantDetails, useCustomMerchant && this.s.show)}>
					<SimpleField dark label="Merchant name" path={`${root}/customMerchant/name`} key="customMerchantName" />
					<MediaField
						dark
						class={this.s.merchantImage}
						label="Merchant image"
						path={`${root}/customMerchant/image`}
						group={this.props.config?.id}
						assetData={{ type: 'homeEnt-merchant' }}
						key="image"
					/>
					<SimpleField
						dark
						label="Merchant image alt text"
						path={`${root}/customMerchant/alt`}
						placeholder={'Logo' + (customMerchantName ? ` of ${customMerchantName}` : '')}
						help="A short description of the image above, for visually impaired users"
						key="customMerchantAlt"
					/>
				</div>
				<SimpleField dark class={this.s.link} label="Product link" path={`${root}/link`} placeholder="https://example.com" />
				<Button dark text danger class={this.s.delete} onClick={() => this.deleteItem(section, item)}>Delete item</Button>
			</div>
		);
	}

	renderSectionSettings(section) {
		const readonly = !canUser('write');
		const root = `${this.props.dataRoot}/content/pages/homeEnt/sections/${section}`;
		const data = this.getData()?.sections?.[section];

		return (
			<div class={this.s.sectionSettings} data-section={section}>
				<SimpleField dark label="Section title" path={`${root}/title`} key="title" />
				<div class={this.s.items}>
					<div class={this.s.note}>Section items</div>
					<Sortable class={this.s.list} onChange={this.onSectionItemsSort} disabled={readonly}>
						{sortObjectByOrder(data?.items, (item, itemId) => (
							<SortableItem class={this.s.itemWrapper} key={itemId} data-item={itemId}>
								<Handle class={this.s.handle} />
								<div class={this.s.item}>
									<span>{this.getMerchantName(item)}</span>
									<button class={this.s.edit} onClick={() => this.selectItem(section, itemId)}>Edit</button>
								</div>
								<Button class={this.s.listDelete} icon="cancel" title="Delete" onClick={() => this.deleteItem(section, itemId)} />
							</SortableItem>
						))}
					</Sortable>
					<Button class={this.s.add} dark primary icon="plus" onClick={() => this.addItem(section)} disabled={readonly}>
						Add item
					</Button>
				</div>
				<div>
					<ToggleField
						disabled={readonly}
						label="Show disclaimer"
						path={`${root}/showDisclaimer`}
					>
						Show disclaimer
					</ToggleField>
					<SimpleField
						dark
						multiline
						disabled={readonly || !data?.showDisclaimer}
						path={`${root}/disclaimer`}
						placeholder={DEFAULT_DISCLAIMER_TEXT}
					/>
				</div>
				<Button dark text danger class={this.s.delete} onClick={() => this.deleteSection(section)}>Delete section</Button>
			</div>
		);
	}

	renderSectionsSettings() {
		const data = this.getData();
		const readonly = !canUser('write');

		return (
			<div class={this.s.sectionsSettings}>
				<div class={this.s.items}>
					<div class={this.s.note}>Sections</div>
					<Sortable class={this.s.list} onChange={this.onSectionsSort} disabled={readonly}>
						{sortObjectByOrder(data?.sections, (section, id) => (
							<SortableItem class={this.s.itemWrapper} key={id} data-section={id}>
								<Handle class={this.s.handle} />
								<div class={this.s.item}>
									<span>{section?.title}</span>
									<button class={this.s.edit} onClick={() => this.selectSection(id)}>Edit</button>
								</div>
								<Button class={this.s.listDelete} icon="cancel" title="Delete" onClick={() => this.deleteSection(id)} />
							</SortableItem>
						))}
					</Sortable>
					<Button class={this.s.add} dark primary icon="plus" onClick={this.addSection} disabled={readonly}>
						Add section
					</Button>
				</div>
			</div>
		);
	}

	// ----- HEADER ----- //

	// Header Utilities

	selectFilter(filter) {
		if (!filter) return;

		this.props.select(`/pages/homeEnt/filters/${filter}`);
	}

	async resetFilter(filter) {
		if (!filter) return;

		const promptResult = await confirm('This will reset the filter to its default text.', { title: 'Reset filter override?', button: 'Reset', danger: true });
		if (!promptResult) return;
		
		const basePath = this.props.dataRoot || '';
		jsonPointer.removeDeep(Store.get().data, `${basePath}/content/pages/homeEnt/filters/${filter}`);

		this.props.select(`/pages/homeEnt/header`);
	}

	async resetFilters() {
		const promptResult = await confirm('This will reset all filters to their default text.', { title: 'Reset filter overrides?', button: 'Reset', danger: true });
		if (!promptResult) return;

		const basePath = this.props.dataRoot || '';
		jsonPointer.setDeep(Store.get().data, `${basePath}/content/pages/homeEnt/filters`, null);
	}

	// Render Visuals

	/** OVERRIDE */
	renderTopLinks() 	{
		// Only show "See in Theaters" link if tickets are on sale for the movie
		const theaterStatus = this.movie?.status?.theaters;
		const hasTicketsOnSale = theaterStatus === 'preSale' || theaterStatus === 'inTheaters';

		return (
			<div class={this.s.topLinks}>
				{hasTicketsOnSale && (
					<div class={this.s.topLink}>
						{this.text('$SEE_IN_THEATERS', 'See in Theaters')}
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
							<path fill-rule="evenodd" clip-rule="evenodd" d="M6.66371 4.43896L9.89599 1.20663L10.7925 2.10317C10.2268 2.98171 10.3278 4.13312 11.0974 4.90272C11.8679 5.67233 13.0183 5.77327 13.8969 5.20756L14.7934 6.1041L11.5611 9.33644L6.66371 4.43896ZM6.10401 14.7936L5.20748 13.8961C5.77418 13.0185 5.67324 11.8671 4.90265 11.0975C4.13305 10.3279 2.98167 10.227 2.10413 10.7927L1.20661 9.89515L5.95709 5.1456L10.8545 10.0431L6.10401 14.7936ZM15.8538 5.75029L14.2877 4.18509C14.0928 3.99019 13.7769 3.99019 13.581 4.18509C13.0793 4.68583 12.2987 4.69083 11.804 4.19609C11.3092 3.70134 11.3142 2.92074 11.815 2.419C12.0099 2.2241 12.0099 1.90727 11.815 1.71237L10.2488 0.146175C10.0539 -0.0487249 9.73808 -0.0487249 9.54218 0.146175L5.95709 3.73233L5.25046 4.43896L0.146172 9.54233C-0.0487241 9.73723 -0.0487241 10.0541 0.146172 10.249L1.71234 11.8142C1.90824 12.0101 2.22407 12.0101 2.41896 11.8142C2.9207 11.3134 3.70128 11.3094 4.19602 11.8042C4.69076 12.2989 4.68576 13.0795 4.18603 13.5812C3.99013 13.7761 3.99013 14.093 4.18603 14.2879L5.75119 15.8531C5.94609 16.049 6.26292 16.049 6.45782 15.8531L11.5611 10.7507L12.2677 10.0431L15.8538 6.45792C16.0487 6.26202 16.0487 5.94618 15.8538 5.75029Z" />
						</svg>
					</div>
				)}
			</div>
		);
	}

	/** OVERRIDE */
	renderMainHeader() {
		return (
			<MoviePageHeader {...this.props} />
		);
	}

	// Render Settings

	/** OVERRIDE */
	renderHeaderSettings() {
		const data = this.movie?.pages?.homeEnt;
		const hasOverrides = Object.values(data?.filters || {}).some(filter => filter.title);

		return (
			<div class={this.s.headerSettingsWrapper}>
				<div class={this.s.filtersSettings}>
					<div class={this.s.items}>
						<div class={this.s.note}>Filters</div>
						<div class={this.s.list}>
							{sortObjectByOrder(data?.sections, (section, id) => (
								<div class={this.s.itemWrapper} key={id} data-filter={id}>
									<div class={this.s.item}>
										<span>{data?.filters?.[id]?.title || section?.title}</span>
										<button class={this.s.edit} onClick={() => this.selectFilter(id)}>Edit</button>
									</div>
								</div>
							))}
						</div>
					</div>
					<Button dark danger text class={this.s.remove} disabled={!hasOverrides} onClick={this.resetFilters} key="reset-filters">Reset filter overrides</Button>
				</div>
				{super.renderHeaderSettings()}
			</div>
		);
	}

	renderFilterSettings(id) {
		const readonly = !canUser('write');
		const root = `${this.props.dataRoot}/content/pages/homeEnt/filters/${id}`;

		const hasOverride = !!this.getData()?.filters?.[id]?.title;
		const sectionData = this.getData()?.sections?.[id];

		return (
			<div class={this.s.filterSettings}>
				<SimpleField dark disabled={readonly} label="Filter title" path={`${root}/title`} placeholder={sectionData?.title} key="title" />
				<div class={this.s.linkedSection}>
					<span class={this.s.title}>Linked section</span>
					<span class={this.s.item}><LinkIcon />{sectionData?.title || 'None'}</span>
				</div>
				<Button dark danger text class={this.s.remove} disabled={readonly || !hasOverride} onClick={() => this.resetFilter(id)} key="reset-filter">Reset filter</Button>
			</div>
		);
	}
};